import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { leadEntryValid, sameUnit, stockSaveValid } from '@d-orca/shared/client/logistics';
import { parseNumber } from '@d-orca/shared/client/utils';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { useComboDismiss } from '@hooks/useComboDismiss';
import { fold, unitOptions } from '../units';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import type { DeliveredAmount } from '../deliveryAmounts';
import {
  buildSnapshotBody,
  openingLead,
  seedMap,
  computeSyncMerge,
  estimateWaste,
} from './StockEntryDialog.logic';
import type { IStockEntryFormProps } from './StockEntryDialog.types';
import { StockEntryMaterialField } from './StockEntryMaterialField';
import { SimilarNameGuardModal } from './SimilarNameGuardModal';
import { rankNameOptions } from './similarMaterials';
import { useSimilarNameGuard } from './useSimilarNameGuard';
import { StockEntryLeadField } from './StockEntryLeadField';
import { StockEntryAreaField } from './StockEntryAreaField';
import { useStockAreaAssignment } from './useStockAreaAssignment';
import { useStockEntrySave } from './useStockEntrySave';
import { StockEntrySyncFields } from './StockEntrySyncFields';
import { StockEntryAmountUnitFields } from './StockEntryAmountUnitFields';
import { StockEntrySpanField } from './StockEntrySpanField';
import { shownUnit, withUnitLabels } from './stockUnits';
import { useSubmitOnce } from './useSubmitOnce';
import styles from './StockEntryDialog.module.css';

export function StockEntryForm({
  siteId,
  prefill: storedPrefill,
  mode,
  arrived,
  estimatedRemaining,
  siteMedianLeadDays,
  leadsByMaterial,
  unitsByMaterial,
  deliverySuggestions,
  trackedByName,
  onOpenExisting,
  deliveredAmounts,
  onClose,
  arrivals,
  unitMismatchActions,
  secondaryAction,
}: IStockEntryFormProps) {
  const { t } = useTranslation();
  // Units in the viewer's labels from here on — see `withUnitLabels`.
  const prefill = withUnitLabels(storedPrefill, t);
  const { saveLocked } = useLogisticsEditLock();
  const submitOnce = useSubmitOnce();
  const unitList = unitOptions(t);
  const { toasts, push, dismiss } = useToasts();
  const [materialName, setMaterialName] = useState(() => prefill?.materialName ?? '');
  const [unit, setUnit] = useState(() => prefill?.unit ?? '');
  const [amount, setAmount] = useState(() => (prefill?.amount != null ? String(prefill.amount) : ''));
  const [spanDays, setSpanDays] = useState(() => (prefill?.spanDays != null ? String(prefill.spanDays) : ''));
  // The same resolution the caption quotes (`openLead` below), so box and caption agree.
  const initialLead = () => openingLead(prefill, siteMedianLeadDays, leadsByMaterial, prefill?.materialName);
  const [leadDays, setLeadDays] = useState(() => initialLead().days?.toString() ?? '');
  /**
   * The lead value the form itself last wrote into the field — the yardstick
   * for "did the user change it". Tracked as state, NOT recomputed from the
   * material name: the field's value only retargets when a suggestion is
   * actually PICKED, so a live recomputation drifts off it the moment someone
   * merely TYPES a tracked material's name in full. That drift made the save
   * path read the untouched site-median seed as a deliberate entry and store
   * it — a borrowed average masquerading as this material's own estimate
   * until its first delivery is measured.
   */
  const [leadSeeded, setLeadSeeded] = useState(() => initialLead().days?.toString() ?? '');
  // Set ONLY by confirming the combobox's add-new row. Typing an unmatched name
  // isn't a declaration — the user may be mid-word or about to pick a suggestion.
  const [declaredNew, setDeclaredNew] = useState(false);
  // The arrival an amount was prefilled from, so the field can say where its
  // number came from. A figure that appears on its own reads as the system's
  // claim about the yard; naming the order makes it checkable.
  const [fromDelivery, setFromDelivery] = useState<DeliveredAmount | null>(null);
  // Sync only. Seeded from the estimate and expected to be corrected — it is the
  // one number that makes the closing cycle a measurement rather than a replay
  // of the estimate the system already held.
  const [remaining, setRemaining] = useState(() =>
    estimatedRemaining != null ? String(estimatedRemaining) : '',
  );
  // The span follows the merged amount while it is still a derived figure, and
  // stops the moment the user types their own — a field that keeps overwriting
  // a deliberate entry is worse than one that is occasionally stale.
  const [spanTouched, setSpanTouched] = useState(false);
  // Only used when the arrival recorded no amount. Genba supplies none, so every
  // delivery predating the quantity field lands here — the user types what was
  // actually unloaded rather than being stranded.
  const [arrivedQty, setArrivedQty] = useState('');
  // Sync only. What the user typed into Waste, or null while the field still
  // tracks its seed — the seed follows the remaining count live (a tenth of
  // the batch, capped at what is left), and stops the moment the user types
  // their own figure, like the span field does.
  const [wasteInput, setWasteInput] = useState<string | null>(null);
  /**
   * What the SYSTEM put in each field, keyed by field name.
   *
   * A prefilled figure is a suggestion the user is expected to check, not an
   * answer — amber says "we guessed this" and the colour drops the moment the
   * value stops being ours. Comparing against the seeded string (rather than a
   * touched flag) means retyping the same number is honestly still our number,
   * and clearing a field stops claiming we filled it.
   */
  const [seeded, setSeeded] = useState<Record<string, string>>(() =>
    seedMap(prefill, estimatedRemaining, mode, initialLead()),
  );
  /** What the material already held, as the strings its fields render as. */
  const previous: Record<string, string> = {};
  if (prefill?.previous?.unit != null) previous.unit = prefill.previous.unit;
  if (prefill?.previous?.amount != null) previous.amount = String(prefill.previous.amount);
  if (prefill?.previous?.spanDays != null) previous.spanDays = String(prefill.previous.spanDays);
  if (prefill?.previous?.estimatedLeadTimeDays != null) {
    previous.leadDays = String(prefill.previous.estimatedLeadTimeDays);
  }

  /**
   * Amber while the field still holds exactly what we put there — UNLESS that is
   * also what the material already held. Carrying a stored figure forward is not
   * an estimate, and colouring it made most of the form look inferred when only
   * a field or two actually was. Amber has to mean "we changed this" or it means
   * nothing.
   */
  const isSeeded = (field: string, value: string): boolean =>
    seededNow[field] !== undefined && seededNow[field] === value && seededNow[field] !== previous[field];
  const seededCls = (field: string, value: string): string =>
    isSeeded(field, value) ? ` ${styles.prefilled}` : '';
  /**
   * Once the user overrides a seeded value, the figure we suggested is gone from
   * the screen — and it is the thing they are deciding against. This keeps it
   * visible beneath the field so the override stays a comparison, not a
   * replacement, and so a mistaken edit can be undone from memory.
   *
   * Nothing renders while the field still holds our value (the amber input says
   * that already), nor for a field we never seeded.
   */
  const seedNote = (field: string, value: string) => {
    const was = seededNow[field];
    if (was === undefined || was === value) return null;
    return <span className={styles.seedNote}>{t('logistics.stockDialog.wasEstimated', { value: was })}</span>;
  };
  // How the unit value got set: picked/prefilled → reopen shows the FULL list;
  // typed → the list narrows. (Value-based detection can't tell 't' typed
  // mid-search from 't' selected — the exact bug this flag fixes.)
  const [unitPicked, setUnitPicked] = useState(() => prefill?.unit != null);
  const materialComboRef = useRef<HTMLDivElement | null>(null);
  const unitComboRef = useRef<HTMLDivElement | null>(null);
  const materialCombo = useComboDismiss(materialComboRef);
  const unitCombo = useComboDismiss(unitComboRef);

  // Unit is pick-only: typing FILTERS the options, but the value must resolve
  // to a real option (exact fold match) — free text is never a unit. A prefilled
  // legacy unit outside the presets stays offered so re-estimates don't break.
  const prefillUnit = prefill?.unit;
  const allUnitOptions =
    prefillUnit && !unitList.includes(prefillUnit) ? [prefillUnit, ...unitList] : unitList;
  const unitChoice = allUnitOptions.find((o) => sameUnit(o, unit.trim())) ?? null;
  // Picked value → full list on reopen (select mental model); typed text →
  // prefix-narrowed ('t' shows only 't', not every label containing a t).
  const visibleUnitOptions = unitPicked
    ? allUnitOptions
    : allUnitOptions.filter((o) => fold(o).startsWith(fold(unit.trim())));

  const query = materialName.trim();
  const foldedQuery = fold(query);
  // Delivery-name suggestions, ranked fuzzily (similar-name design 2026-08-31).
  const ranked = rankNameOptions(query, deliverySuggestions);
  const materialOptions = ranked.options;
  const trackedHit = prefill?.previous == null ? (trackedByName?.(query) ?? null) : null;
  // Folded equality: typing "鉄筋 d16" against suggested "鉄筋 D16" must NOT
  // offer a duplicate-creating add-new row.
  const exactMatch = materialOptions.some((s) => fold(s) === foldedQuery);

  // ---- sync: one count has to become the old stock plus what landed on it ----
  // Sync mode is decided by the MODE, not by whether the delivery happened to
  // record an amount. Gating on the amount let the row offer Sync while the
  // form quietly rendered its plain shape and the save failed schema
  // validation — a dead end behind a generic "Couldn't save stock". The merge
  // and every derived figure are computed in `computeSyncMerge` (pure).
  const isSync = mode === 'sync';
  const {
    remainingNum,
    remainingValid,
    hasArrival,
    arrivedKnown,
    arrivedNum,
    arrivedValid,
    merged,
    amountNum,
    unitMismatch,
    derivedSpan,
    spanValue,
  } = computeSyncMerge({
    isSync,
    remaining,
    amount,
    arrived,
    arrivedQty,
    unit,
    spanDays,
    spanTouched,
    prefill,
  });
  // The Waste seed, recomputed from the CURRENT remaining count: a leftover the
  // user corrects downward pulls the cap down with it. The stored amount is
  // the batch the cycle opened with — the base of the 10% rule.
  const startingStock = prefill?.previous?.amount ?? null;
  const wasteEstimate = isSync ? estimateWaste(startingStock, remainingNum) : null;
  const wasteSeed = wasteEstimate != null ? String(wasteEstimate) : '';
  const waste = wasteInput ?? wasteSeed;
  const wasteNum = waste.trim() === '' ? null : parseNumber(waste);
  // The seed map plus the one live seed, so amber and the "Estimated N" note
  // read the waste field the same way they read every other seeded field.
  const seededNow: Record<string, string> =
    isSync && wasteSeed !== '' ? { ...seeded, waste: wasteSeed } : seeded;

  const spanNum = parseNumber(spanValue);
  // The shared Save rule (`stockSaveValid`) — both clients judge a count alike.
  const valid = stockSaveValid({
    isSync,
    query,
    unitResolved: unitChoice != null,
    remainingValid,
    arrivedValid,
    wasteNum,
    remainingNum,
    amountNum,
    spanNum,
  });
  const ratePreview = valid ? Math.round((amountNum / spanNum) * 10) / 10 : null;

  /**
   * What the material was running at before — BOTH figures, shown whenever the
   * span on screen is no longer the stored one.
   *
   * Keyed off the SPAN, not the rate. Keying it off the rate hid the note in the
   * one case it is most wanted: 14 cans over 7 days and 10 over 5 are both
   * 2/day, so a changed span with an unchanged rate showed nothing at all — the
   * field was visibly different and the row would not say from what.
   *
   * Both numbers because either alone is ambiguous: the span without the rate
   * cannot tell a bigger pile from faster use, and the rate without the span
   * does not say how long the old estimate ran.
   */
  const previousRate =
    prefill?.previous?.amount != null && prefill.previous.spanDays
      ? Math.round((prefill.previous.amount / prefill.previous.spanDays) * 10) / 10
      : null;
  /**
   * The lead field is offered on EVERY material.
   *
   * It used to appear only where nothing had been measured, which meant the one
   * number the reorder point is derived from was invisible — and uncorrectable —
   * exactly where the system had an opinion about it. A measurement can be
   * wrong (one freak delivery, an imported row with a bogus date) and the only
   * remedy was deleting deliveries.
   *
   * REQUIRED only when nothing can stand in for it: no measurement, no stored
   * entry, no site average. Then the reorder point cannot be derived at all and a
   * guess is the only way to get one. Everywhere else the field arrives
   * prefilled, and clearing it is a legitimate answer meaning "use whatever you
   * have" — which is what sends `null` below.
   */
  const openLead = openingLead(prefill, siteMedianLeadDays, leadsByMaterial, materialName);
  const leadRequired = openLead.days == null && declaredNew && !exactMatch;
  const leadNum = parseNumber(leadDays);
  // Whole days 0–365, never blank — see `leadEntryValid` for why 0 counts.
  const leadValid = leadEntryValid(leadDays);
  // Blank is a real answer; anything else has to be a usable number.
  const leadOk = leadDays.trim() === '' ? !leadRequired : leadValid;
  /** The field still holds exactly what the form put there — see `leadSeeded`
   *  for why this compares against the seeded value, not a live recomputation. */
  const leadUntouched = leadDays.trim() === leadSeeded.trim();

  /**
   * A stock estimate shorter than the lead time cannot be kept topped up. The
   * material runs dry before a reorder can land — every cycle, whatever day the
   * order is placed — so this is arithmetic, not a preference.
   *
   * Silent when no lead is known: a warning derived from a guessed lead would
   * fire hardest on the materials the system knows least about.
   */
  // The lead ON SCREEN, not the stored one. The field is editable now, so a
  // warning quoting "restocking takes ~16d" above a box the user has just
  // changed to 8 contradicts itself in the same glance.
  const lead = leadValid ? leadNum : openLead.days;
  /**
   * It states the shortfall and stops there. It used to close with "about 320
   * roll would cover it" — `rate x lead`, the amount that would have to be on
   * site for a reorder to land in time — which is advice about a PURCHASE
   * delivered in a form that only records a COUNT. Nobody chooses the amount
   * here; they type what is physically on the ground. The reader could not act
   * on the number without leaving, and the place they would go already carries
   * it: the row flips to "Order now" and that dialog's quantity is sized
   * against the same lead time (`orderPrefill.coveringQuantity`).
   *
   * Suppressed under a merge for the adjacent reason — there the amount is
   * arithmetic on two facts, what is left plus what arrived, so the span is a
   * consequence rather than anything the user proposed.
   */
  const unsustainable =
    !hasArrival && lead != null && Number.isInteger(spanNum) && spanNum > 0 && spanNum < lead;

  const spanChanged =
    previousRate != null && previous.spanDays !== undefined && previous.spanDays !== spanValue;

  // The label a rate/warning quotes — resolved once, reused across the span
  // field's rate preview, sustainability note, and "was N days" comparison.
  const unitLabel = unitChoice ?? unit.trim();

  /* "Area stored" (SLS site map) — the query, the derived containing area,
     the stale-proof explicit choice, and the post-snapshot move all live in
     `useStockAreaAssignment`. */
  const area = useStockAreaAssignment(siteId, foldedQuery);

  const save = useStockEntrySave({
    siteId,
    area,
    // Payload shaping (PATCH discipline: only changed fields ride along)
    // lives in `buildSnapshotBody` — pure and tested on its own.
    buildBody: () =>
      buildSnapshotBody({
        query,
        mode,
        prefill,
        unitLabel,
        amountNum,
        spanNum,
        leadDays,
        leadUntouched,
        leadNum,
        remainingNum,
        wasteNum,
        arrivals,
      }),
    query,
    push,
    onClose,
  });

  const pick = (name: string, isNew = false) => {
    setMaterialName(name);
    setDeclaredNew(isNew);
    // An existing material carries its unit — prefill it so re-snapshots stay
    // unit-consistent. Delivery names / new materials leave the unit alone.
    const lastUnit = shownUnit(t, unitsByMaterial[name]);
    if (lastUnit) setUnit(lastUnit);

    // ...and its own lead. Only on a blank "Add stock": a row-tap prefill
    // already arrived with the right one and the user may have edited it since.
    if (!prefill) {
      const lead = openingLead(null, siteMedianLeadDays, leadsByMaterial, name);
      setLeadDays(lead.days?.toString() ?? '');
      setLeadSeeded(lead.days?.toString() ?? '');
      setSeeded((prev) => {
        const next = { ...prev };
        // Same rule as the seed map: a measurement or a borrowed average is
        // something the system worked out; the user's own entry is not.
        if (lead.days != null && lead.source !== 'estimated') next.leadDays = String(lead.days);
        else delete next.leadDays;
        return next;
      });
    }

    // What the latest arrival brought. The amount is prefilled outright — there
    // is no competing source for it — but a material already being tracked keeps
    // ITS unit: re-snapshotting in a different unit than the running estimate
    // would silently change what daysLeft means.
    const landed = deliveredAmounts[fold(name)];
    const landedUnit = shownUnit(t, landed?.unit);
    setFromDelivery(landed ?? null);
    if (landed) {
      setAmount(String(landed.quantity));
      if (!lastUnit && landedUnit) setUnit(landedUnit);
    }
    // These are system-supplied too, so they carry the same amber as a prop
    // prefill. Anything the pick did NOT set is cleared from the map — a stale
    // entry would keep colouring a value the user has since owned.
    setSeeded((prev) => {
      const next = { ...prev };
      const pickedUnit = lastUnit ?? landedUnit ?? null;
      if (pickedUnit) next.unit = pickedUnit;
      else delete next.unit;
      if (landed) next.amount = String(landed.quantity);
      else delete next.amount;
      return next;
    });
    materialCombo.setOpen(false);
  };

  const pickUnit = (u: string) => {
    setUnit(u);
    setUnitPicked(true);
    unitCombo.setOpen(false);
  };

  const guard = useSimilarNameGuard({
    query,
    closeMatch: ranked.closeMatch,
    isNewMaterial: prefill?.previous == null,
    onUseExisting: pick,
    onSave: () => submitOnce(() => save.mutate()),
  });

  return (
    <>
      {/* Named once, at the top, rather than left for the user to infer
                from the colour. Only when something actually WAS prefilled — on
                a blank "Add stock" there is no estimate to disclaim, and a
                standing banner would train people to stop reading it.

                It names the COLOUR, not a source. The fields reaching it come
                from three different places — learned cycles, the running
                estimate under a merge, and a delivery order for a material
                nobody has counted — and the old copy asserted "based on history
                events" for all of them, which is false on two. */}
      {Object.keys(seeded).length > 0 ? (
        <p className={styles.prefillBanner}>{t('logistics.stockDialog.prefillNote')}</p>
      ) : null}

      <StockEntryMaterialField
        comboRef={materialComboRef}
        open={materialCombo.open}
        onInputChange={(v) => {
          setMaterialName(v);
          materialCombo.setOpen(true);
        }}
        onInputClick={materialCombo.tryOpen}
        onCaretClick={materialCombo.toggle}
        onBlurAway={() => materialCombo.setOpen(false)}
        value={materialName}
        className={`${styles.input} ${styles.comboInput}${seededCls('materialName', materialName)}`}
        // An existing tracked material is being reconciled (Sync) or
        // re-counted (row click) — both carry a `previous` block — so the
        // material is fixed and the field is locked. Only the new-material
        // flows (blank "Add stock", add-from-untracked) let you choose it.
        locked={prefill?.previous != null}
        options={materialOptions}
        closeMatch={ranked.closeMatch}
        closeMatches={ranked.closeMatches}
        trackedHit={trackedHit}
        onOpenExisting={onOpenExisting}
        query={query}
        exactMatch={exactMatch}
        onPick={pick}
        t={t}
      >
        <StockEntryAmountUnitFields
          hasArrival={hasArrival}
          isSync={isSync}
          amount={amount}
          onAmountChange={setAmount}
          amountClassName={`${styles.input}${seededCls('amount', amount)}`}
          fromDelivery={fromDelivery}
          amountSeedNote={seedNote('amount', amount)}
          unitComboRef={unitComboRef}
          unitComboOpen={unitCombo.open}
          onUnitBlurAway={() => unitCombo.setOpen(false)}
          unit={unit}
          unitClassName={`${styles.input} ${styles.comboInput}${seededCls('unit', unit)}`}
          onUnitInputChange={(v) => {
            setUnit(v);
            setUnitPicked(false);
            unitCombo.setOpen(true);
          }}
          onUnitInputClick={unitCombo.tryOpen}
          onUnitCaretClick={unitCombo.toggle}
          visibleUnitOptions={visibleUnitOptions}
          onPickUnit={pickUnit}
          unitSeedNote={seedNote('unit', unit)}
          t={t}
        />
      </StockEntryMaterialField>

      <StockEntryLeadField
        value={leadDays}
        onChange={setLeadDays}
        className={`${styles.input} ${styles.spanInput}${seededCls('leadDays', leadDays)}`}
        seedNote={seedNote('leadDays', leadDays)}
        leadUntouched={leadUntouched}
        openLeadSource={openLead.source}
        openLeadDays={openLead.days}
        t={t}
      />

      <StockEntryAreaField
        areas={area.areas}
        value={area.chosenAreaId}
        onChange={area.choose}
        naLabel={area.naLabel}
        t={t}
      />

      <StockEntrySyncFields
        isSync={isSync}
        remaining={remaining}
        onRemainingChange={setRemaining}
        remainingClassName={`${styles.input}${seededCls('remaining', remaining)}`}
        remainingSeedNote={seedNote('remaining', remaining)}
        waste={waste}
        onWasteChange={setWasteInput}
        wasteClassName={`${styles.input}${seededCls('waste', waste)}`}
        wasteSeedNote={seedNote('waste', waste)}
        unit={unit}
        hasArrival={hasArrival}
        arrivedKnown={arrivedKnown}
        arrivedQty={arrivedQty}
        onArrivedQtyChange={setArrivedQty}
        arrived={arrived}
        unitMismatch={unitMismatch}
        merged={merged}
        remainingValid={remainingValid}
        remainingNum={remainingNum}
        arrivedValid={arrivedValid}
        arrivedNum={arrivedNum}
        material={query}
        actions={unitMismatchActions}
        t={t}
      />

      <StockEntrySpanField
        value={spanValue}
        onChange={(v) => {
          setSpanTouched(true);
          setSpanDays(v);
        }}
        highlighted={
          isSeeded('spanDays', spanValue) ||
          (hasArrival && !spanTouched && derivedSpan != null && String(derivedSpan) !== previous.spanDays)
        }
        // The derived span is not in the seed map, so its "Estimated N"
        // override note is computed here: shown once the field no longer
        // holds our derived value (and only when we actually suggested a
        // change from the stored span).
        estimatedSpan={
          hasArrival &&
          derivedSpan != null &&
          String(derivedSpan) !== spanValue &&
          String(derivedSpan) !== previous.spanDays
            ? derivedSpan
            : null
        }
        ratePreview={ratePreview}
        unitLabel={unitLabel}
        lead={lead}
        unsustainable={unsustainable}
        spanNum={spanNum}
        amountNum={amountNum}
        spanChanged={spanChanged}
        wasSpanDays={prefill?.previous?.spanDays}
        previousRate={previousRate}
        t={t}
      />

      <SimilarNameGuardModal
        guard={guard}
        typed={query}
        body={t('logistics.similarName.bodyStock', { typed: query, existing: guard.match ?? '' })}
        t={t}
      />
      <div className={styles.footer}>
        {secondaryAction}
        <button
          type="button"
          className={styles.saveBtn}
          disabled={!valid || !leadOk || save.isPending || trackedHit != null || saveLocked}
          onClick={guard.onSaveClick}
        >
          {t('logistics.stockDialog.save')}
        </button>
      </div>
      {createPortal(<Toasts toasts={toasts} onDismiss={dismiss} />, document.body)}
    </>
  );
}
